import { FC, useEffect, useRef, useState } from 'react';
import { BvIcon } from 'bevi-icon';
import { useSimulationBank } from '../../../../../providers/SimulationBank/SimulationBankProvider';
import { FinanceiraCard, FinanceiraStatus } from '../../../../../models/SimulationBank/SimulationBankModels';
import FormatMoney from '../../../../../utils/FormatMoney';
import StepNavigation from '../../components/StepNavigation/StepNavigation';
import refreshIcon from '../../../../../assets/icons/refresh.svg';
import styles from './Step3Cards.module.scss';

interface Step3CardsProps {
    onSimulate: () => void;
    onBack: () => void;
}

type TextSeg = { text: string; style?: 'bold' };

const SEM_OFERTA_INTRO: TextSeg[] = [
    { text: 'As financeiras a seguir ' },
    { text: 'não apresentaram propostas válidas', style: 'bold' },
    { text: ' para este cliente.' },
];

const StreamingText: FC<{ segments: TextSeg[]; speed?: number }> = ({ segments, speed = 22 }) => {
    const totalChars = segments.reduce((s, seg) => s + seg.text.length, 0);
    const [count, setCount] = useState(0);

    useEffect(() => {
        setCount(0);
        const iv = setInterval(() => {
            setCount((prev) => {
                const next = Math.min(prev + 3, totalChars);
                if (next >= totalChars) clearInterval(iv);
                return next;
            });
        }, speed);
        return () => clearInterval(iv);
    }, []);

    let rem = count;
    const nodes = segments.map((seg, i) => {
        if (rem <= 0) return null;
        const visible = seg.text.slice(0, rem);
        rem -= seg.text.length;
        if (!visible) return null;
        return seg.style === 'bold' ? <strong key={i}>{visible}</strong> : <span key={i}>{visible}</span>;
    });

    return <>{nodes}</>;
};

const getStatusBadges = (status: FinanceiraStatus, alreadyAuthorized = false): string[] => {
    if (alreadyAuthorized && status !== 'CONCLUIDO') {
        return ['Autorizado'];
    }
    switch (status) {
        case 'PRE_ANALISE': return ['Pré-análise'];
        case 'AGUARDANDO_AUTORIZACAO': return ['Link SCR disponível'];
        case 'AUTORIZADO': return ['Pré-análise', 'Link SCR disponível', 'Autorizado'];
        case 'CONCLUIDO': return ['Pré-análise', 'Autorizado', 'Consulta realizada'];
        default: return ['Pré-análise'];
    }
};

const getStatusText = (status: FinanceiraStatus, nome: string, alreadyAuthorized = false): string => {
    const isCrefaz = nome.toUpperCase().includes('CREFAZ');
    if (alreadyAuthorized && status !== 'CONCLUIDO') {
        return 'Consulta autorizada pelo cliente';
    }
    if (status === 'AUTORIZADO' || status === 'CONCLUIDO') {
        return 'Consulta de margem Autorizada';
    }
    switch (status) {
        case 'PRE_ANALISE':
        case 'AGUARDANDO_AUTORIZACAO':
            return isCrefaz ? 'Link enviado por SMS ao cliente' : 'Copiar o link e enviar ao cliente';
        case 'ERRO': return 'Erro no processamento';
        default: return isCrefaz ? 'Link enviado por SMS ao cliente' : 'Copiar o link e enviar ao cliente';
    }
};

const BMG_CODE_LENGTH = 6;

// Máscara "**•9432" pro telefone do cliente (já coletado no Step1) — usada no card de
// verificação de segurança do BMG pra lembrar o cliente pra qual número o SMS foi enviado.
const maskPhoneLast4 = (telefone?: string | null): string => {
    const digits = (telefone ?? '').replace(/\D/g, '');
    if (digits.length < 4) return '';
    return `${digits.slice(-4)}`;
};

// Peculiaridade do BMG: em vez de aguardar o polling normal, o card pede um código de
// autorização de 6 dígitos enviado por SMS. Ao preencher o último dígito, dispara
// automaticamente a validação (ver onComplete/handleValidateBmgCode).
const BmgCodeInput: FC<{ onComplete: (codigo: string) => void; disabled?: boolean }> = ({ onComplete, disabled }) => {
    const [digits, setDigits] = useState<string[]>(() => Array(BMG_CODE_LENGTH).fill(''));
    const inputsRef = useRef<(HTMLInputElement | null)[]>([]);

    const handleChange = (index: number, value: string) => {
        const clean = value.replace(/\D/g, '').slice(-1);
        const next = [...digits];
        next[index] = clean;
        setDigits(next);
        if (clean && index < BMG_CODE_LENGTH - 1) inputsRef.current[index + 1]?.focus();
        if (clean && next.every((d) => d !== '')) onComplete(next.join(''));
    };

    const handleKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Backspace' && !digits[index] && index > 0) inputsRef.current[index - 1]?.focus();
    };

    // Cola o código inteiro (ex.: copiado do SMS) distribuindo um dígito por caixa, a
    // partir da primeira — sem isso o paste só preenchia a caixa focada com o 1º dígito.
    const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
        const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, BMG_CODE_LENGTH);
        if (!pasted) return;
        e.preventDefault();
        const next = Array(BMG_CODE_LENGTH).fill('');
        for (let i = 0; i < pasted.length; i++) next[i] = pasted[i];
        setDigits(next);
        const lastIndex = Math.min(pasted.length, BMG_CODE_LENGTH) - 1;
        inputsRef.current[lastIndex]?.focus();
        if (next.every((d) => d !== '')) onComplete(next.join(''));
    };

    return (
        <div className={styles.bmgCodeRow} onClick={(e) => e.stopPropagation()}>
            {digits.map((d, i) => (
                <input
                    key={i}
                    ref={(el) => { inputsRef.current[i] = el; }}
                    className={styles.bmgCodeBox}
                    type="text"
                    inputMode="numeric"
                    maxLength={1}
                    value={d}
                    disabled={disabled}
                    onChange={(e) => handleChange(i, e.target.value)}
                    onKeyDown={(e) => handleKeyDown(i, e)}
                    onPaste={handlePaste}
                />
            ))}
        </div>
    );
};

const LinkIcon: FC = () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} width={12} height={12} style={{ flexShrink: 0, color: '#94a3b8' }}>
        <path d="M10 13a5 5 0 007.54.54l3-3a5 5 0 00-7.07-7.07l-1.72 1.71" />
        <path d="M14 11a5 5 0 00-7.54-.54l-3 3a5 5 0 007.07 7.07l1.71-1.71" />
    </svg>
);

interface FinanceiraCardItemProps {
    card: FinanceiraCard;
    onToggle: (id: number | string) => void;
    isPending?: boolean;
    isRefreshing?: boolean;
    semOferta?: boolean;
    clientPhone?: string;
    bmgValidating?: boolean;
    onBmgCodeSubmit?: (codigo: string) => void;
}

const FinanceiraCardItem: FC<FinanceiraCardItemProps> = ({
    card, onToggle, isPending = false, isRefreshing = false, semOferta = false,
    clientPhone, bmgValidating = false, onBmgCodeSubmit,
}) => {
    const [copied, setCopied] = useState(false);
    const [expirou, setExpirou] = useState(false);
    const [userInteractedWithLink, setUserInteractedWithLink] = useState(false);
    const isBmgAwaitingCode = card.canonical_id?.toUpperCase() === 'BMG' && card.status === 'AGUARDANDO_CODIGO';

    const [bmgResetKey, setBmgResetKey] = useState(0);
    const prevBmgValidatingRef = useRef(bmgValidating);
    useEffect(() => {
        if (prevBmgValidatingRef.current && !bmgValidating && isBmgAwaitingCode) {
            setBmgResetKey((k) => k + 1);
        }
        prevBmgValidatingRef.current = bmgValidating;
    }, [bmgValidating, isBmgAwaitingCode]);
    const badges = card.status !== 'ERRO' ? getStatusBadges(card.status, card.already_authorized) : [];
    // Card já autorizado (com ou sem link_url) sai da etapa selecionável de pré-análise,
    // igual a quando o link SCR já foi disponibilizado. Uma vez CONCLUIDO (resultado),
    // o card volta a ser selecionável mesmo que already_authorized/link_url tenham sobrado.
    const hasLinkOrAuthorized = card.status !== 'CONCLUIDO' && !!(card.link_url || card.already_authorized);

    useEffect(() => {
        if (userInteractedWithLink && card.status === 'AGUARDANDO_AUTORIZACAO') {
            const timer = setTimeout(() => setExpirou(true), 6 * 60 * 1000);
            return () => clearTimeout(timer);
        } else if (card.status === 'CONCLUIDO') {
            setExpirou(false);
        }
    }, [userInteractedWithLink, card.status]);

    const handleCopy = (e: React.MouseEvent) => {
        e.stopPropagation();
        if (!card.link_url) return;
        setUserInteractedWithLink(true);
        navigator.clipboard.writeText(card.link_url).then(() => {
            setCopied(true);
            setTimeout(() => setCopied(false), 1800);
        }).catch(() => { });
    };

    return (
        <div
            className={`financeira-card ${card.selecionada ? (card.probabilidade === 'disponivel' ? 'financeira-card--disponivel' : 'financeira-card--selected') : ''} ${card.status === 'ERRO' ? 'financeira-card--error' : ''} ${(hasLinkOrAuthorized || semOferta || isBmgAwaitingCode) ? 'financeira-card--no-select' : ''} ${semOferta ? 'financeira-card--sem-oferta' : ''} ${isBmgAwaitingCode ? styles.logoWrapBmg : isPending ? styles.logoWrapPending : ''}`}
            style={{ cursor: (hasLinkOrAuthorized || semOferta || isBmgAwaitingCode) ? 'default' : undefined, position: 'relative' }}
            onClick={() => !semOferta && !isBmgAwaitingCode && card.status !== 'ERRO' && !hasLinkOrAuthorized && onToggle(card.id)}
        >
            <div className="d-flex gap-3">
                {!isPending && !semOferta && (
                    <div className={`financeira-card__check ${hasLinkOrAuthorized ? styles.checkHidden : styles.checkVisible}`}>
                        <div className={`fin-check ${card.selecionada ? 'fin-check--checked' : ''}`}>
                            {card.selecionada && (
                                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={4}>
                                    <path d="M20 6L9 17l-5-5" />
                                </svg>
                            )}
                        </div>
                    </div>
                )}
                <div className="financeira-card__logo-wrap">
                    {card.logo ? (
                        <img src={card.logo} alt={card.nome} className="financeira-card__logo" />
                    ) : (
                        <span className="financeira-card__logo-fallback">{card.nome.slice(0, 2).toUpperCase()}</span>
                    )}
                </div>
            </div>

            <div className={`d-flex gap-3 ${isPending ? styles.financeiraPendingNoGap : ''}`} style={{ justifyContent: 'space-between', alignItems: 'baseline', width: '100%' }}>
                <div className="financeira-card__info">
                  {isBmgAwaitingCode ? (
                    <div className={styles.bmgAuthWrap}>
                        <div className={styles.cardNameRow}>
                            <strong className="fin-name">{card.nome}</strong>
                            <div className="financeira-card__logo-inline">
                                {card.logoMobile || card.logo ? (
                                    <img src={card.logoMobile || card.logo} alt={card.nome} className="financeira-card__logo" />
                                ) : (
                                    <span className="financeira-card__logo-fallback">{card.nome.slice(0, 2).toUpperCase()}</span>
                                )}
                            </div>
                            <div className={styles.statusRow}>
                                <span className={styles.statusLabel}>STATUS: </span>
                                <span className={styles.statusText}>Verificação de Segurança</span>
                            </div>
                        </div>
                        <span className={styles.bmgAuthSubtitle}>
                            Insira o código de 6 dígitos que enviamos via SMS para o seu número final <strong>{maskPhoneLast4(clientPhone)}</strong>.
                        </span>
                        <div className={styles.bmgCodeInputRow}>
                            <BmgCodeInput key={bmgResetKey} onComplete={(codigo) => onBmgCodeSubmit?.(codigo)} disabled={bmgValidating} />
                            {bmgValidating && <span className={styles.statusSpinner} />}
                        </div>
                        {card.erro && (
                            <div className={styles.authWarning}>
                                <BvIcon name="emergencyHome" style={{ flexShrink: 0, color: '#4F46E5' }} width={16} height={16} />
                                <span className={styles.authWarningText}>{card.erro}</span>
                            </div>
                        )}
                        <span className={styles.bmgTokenWarning}>
                            O token possui um tempo de validade de 5 minutos, após isso ele expirará e deverá ser feita outra consulta.
                        </span>
                    </div>
                  ) : (
                    <>
                    <div className={styles.cardNameRow}>
                        {!isPending && !semOferta && (
                            <div className="financeira-card__check-inline">
                                <div className={`fin-check ${card.selecionada ? 'fin-check--checked' : ''}`}>
                                    {card.selecionada && (
                                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={4}>
                                            <path d="M20 6L9 17l-5-5" />
                                        </svg>
                                    )}
                                </div>
                            </div>
                        )}
                        <strong className="fin-name">{card.nome}</strong>
                        <div className="financeira-card__logo-inline">
                            {card.logoMobile || card.logo ? (
                                <img src={card.logoMobile || card.logo} alt={card.nome} className="financeira-card__logo" />
                            ) : (
                                <span className="financeira-card__logo-fallback">{card.nome.slice(0, 2).toUpperCase()}</span>
                            )}
                        </div>
                        {!semOferta && card.status !== 'ERRO' && !expirou && (
                            <div className={styles.statusRow}>
                                <span className={styles.statusLabel}>STATUS: </span>
                                <span className={styles.statusText}>{getStatusText(card.status, card.nome, card.already_authorized)}</span>
                                {isRefreshing && card.status !== 'CONCLUIDO' && <span className={styles.statusSpinner} />}
                            </div>
                        )}
                    </div>

                    {semOferta ? (
                        card.mensagem_tratada?.trim() ? (
                            <div className={styles.mensagemTratadaWarning}>
                                <BvIcon name="warning" style={{ flexShrink: 0, color: '#B98900' }} width={16} height={16} />
                                <span className={styles.mensagemTratadaWarningText}>{card.mensagem_tratada}</span>
                            </div>
                        ) : (
                            <div className="fin-scr-row">
                                <span className="fin-scr-url fin-scr-url--text">No momento não há ofertas disponíveis para este cliente.</span>
                            </div>
                        )
                    ) : (
                        <>
                            {expirou && card.status === 'AGUARDANDO_AUTORIZACAO' && (
                                <div className={styles.authWarning}>
                                    <BvIcon name="emergencyHome" style={{ flexShrink: 0, color: '#4F46E5' }} width={16} height={16} />
                                    <span className={styles.authWarningText}>Não foi possível processar a autorização, peço tentar novamente mais tarde.</span>
                                </div>
                            )}

                            {!expirou && badges.length > 0 && (
                                <div className="fin-badges">
                                    {badges.map((badge) => (
                                        <span key={badge} className="fin-badge">
                                            <span className="fin-badge__dot" />
                                            {badge}
                                        </span>
                                    ))}
                                </div>
                            )}

                            {!expirou && card.status === 'ERRO' && (
                                <div className="fin-badges">
                                    <span className="fin-badge fin-badge--error">
                                        <span className="fin-badge__dot fin-badge__dot--error" />
                                        {card.erro || 'Erro na consulta'}
                                    </span>
                                </div>
                            )}

                            {!expirou && card.link_url && card.status !== 'CONCLUIDO' && (() => {
                                const isUrl = card.link_url.startsWith('http');
                                return (
                                    <div className="fin-scr-row" onClick={(e) => e.stopPropagation()}>
                                        <LinkIcon />
                                        <span className="fin-scr-label">Link SCR:</span>
                                        {isUrl ? (
                                            <>
                                                <a href={card.link_url} target="_blank" rel="noopener noreferrer" className="fin-scr-url" onClick={() => setUserInteractedWithLink(true)}>
                                                    {card.link_url}
                                                </a>
                                                <button className="fin-scr-copy" onClick={handleCopy}>
                                                    {copied ? 'Copiado!' : 'Copiar link'}
                                                </button>
                                            </>
                                        ) : (
                                            <span className="fin-scr-url fin-scr-url--text">{card.link_url}</span>
                                        )}
                                    </div>
                                );
                            })()}

                            {!expirou && !card.link_url && card.already_authorized && card.status !== 'CONCLUIDO' && (
                                <div className="fin-scr-row">
                                    <span className="fin-scr-url fin-scr-url--text">Aguardando retorno da consulta de margem</span>
                                </div>
                            )}

                            {!expirou && card.status === 'CONCLUIDO' && (() => {
                                // Como o Daycoval e soma devolve o valor da parcela maxima fazemos essa verificação para colocar o label Parcela maxima
                                const isParcelaMaxima = ['SOMA', 'DAYCOVAL'].some((prefixo) => card.nome.toUpperCase().startsWith(prefixo));
                                return (
                                    <div className="fin-results-row">
                                        {card.valor != null && (
                                            <div className="fin-result-item">
                                                <span className="fin-result-label">{isParcelaMaxima ? 'PARCELA MÁXIMA' : 'Valor'}</span>
                                                <span className="fin-result-value">{FormatMoney(card.valor)}</span>
                                            </div>
                                        )}
                                        {(card.prazo_min != null || card.prazo_max != null) && (
                                            <div className="fin-result-item">
                                                <span className="fin-result-label">Prazo</span>
                                                <span className="fin-result-value">
                                                    {card.prazo_min != null && card.prazo_max != null && card.prazo_min !== card.prazo_max
                                                        ? `${card.prazo_min}x – ${card.prazo_max}x`
                                                        : `${card.prazo_min ?? card.prazo_max}x`}
                                                </span>
                                            </div>
                                        )}
                                        {!isParcelaMaxima && (card.parcela_min != null || card.parcela_max != null) && (
                                            <div className="fin-result-item">
                                                <span className="fin-result-label">Parcela</span>
                                                <span className="fin-result-value">
                                                    {card.parcela_min != null && card.parcela_max != null && card.parcela_min !== card.parcela_max
                                                        ? `${FormatMoney(card.parcela_min)} – ${FormatMoney(card.parcela_max)}`
                                                        : FormatMoney(card.parcela_min ?? card.parcela_max ?? 0)}
                                                </span>
                                            </div>
                                        )}
                                    </div>
                                );
                            })()}
                        </>
                    )}
                    </>
                  )}
                </div>
            </div>
        </div>
    );
};

const SortIcon: FC = () => (
    <span className={styles.sortIcon}>
        <span className={styles.sortIconTop}>
            <BvIcon name="arrowNorth" color="#25CBDB" width={16} height={18} weight={600} />
        </span>
        <span className={styles.sortIconBottom}>
            <BvIcon name="arrowSouth" color="#222343" width={16} height={18} weight={600} />
        </span>
    </span>
);

const ChevronIcon: FC<{ open: boolean }> = ({ open }) => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={3} className={`${styles.chevronIcon} ${open ? styles.chevronOpen : ''}`}>
        <path d="M6 9l6 6 6-6" />
    </svg>
);

const InfoWarning: FC<{ message: string }> = ({ message }) => (
    <div className={styles.infoWarning}>
        <BvIcon name="info" variant="dark" style={{ flexShrink: 0, color: '#222343' }} width={18} height={18} />
        <span className={styles.infoWarningText}>{message}</span>
    </div>
);

const Step3Cards: FC<Step3CardsProps> = ({ onSimulate, onBack }) => {
    const { financeiras, isPolling, hasOptionalData, toggleFinanceira, handleScreeningPoll, formData, isValidatingBmg, handleValidateBmgCode } = useSimulationBank();

    const [pendenteOpen, setPendenteOpen] = useState(true);
    const [altaOpen, setAltaOpen] = useState(true);
    const [baixaOpen, setBaixaOpen] = useState(true);
    const [disponivelOpen, setDisponivelOpen] = useState(true);
    const [cooldown, setCooldown] = useState(0);

    const selecionadas = financeiras.filter((f) => !f.hidden && f.selecionada);
    const pendentes = financeiras.filter((f) => !f.hidden && f.status !== 'CONCLUIDO');
    const concluidas = financeiras.filter((f) => !f.hidden && f.status === 'CONCLUIDO');
    const hasResults = concluidas.length > 0;
    const alta = concluidas.filter((f) => f.probabilidade === 'alta');
    const baixa = concluidas.filter((f) => f.probabilidade === 'baixa');
    const disponivel = concluidas.filter((f) => f.probabilidade === 'disponivel');
    const hasAnyCards = pendentes.length > 0 || concluidas.length > 0;
    const semOferta = financeiras.filter((f) => f.hidden);

    useEffect(() => {
        let cancelled = false;
        let timer: ReturnType<typeof setTimeout>;

        const poll = async () => {
            const { done } = await handleScreeningPoll();
            if (cancelled || done) return;
            timer = setTimeout(poll, 10_000);
        };

        timer = setTimeout(poll, 10_000);
        return () => { cancelled = true; clearTimeout(timer); };
    }, [handleScreeningPoll]);

    useEffect(() => {
        if (cooldown <= 0) return;
        const timer = setTimeout(() => setCooldown((c) => c - 1), 1000);
        return () => clearTimeout(timer);
    }, [cooldown]);

    const handleAtualizar = () => {
        if (cooldown > 0) return;
        handleScreeningPoll();
        setCooldown(30);
    };

    return (
        <div className="step-cards">
            <div className={styles.headerInfo}>
                <div style={{ display: 'flex', gap: '6px', alignItems: 'center', minWidth: 0 }}>
                    <BvIcon name="info" variant="dark" style={{ minWidth: 16, flexShrink: 0, color: '#222343' }} width={16} height={16} />
                    <span style={{ fontSize: '13px', color: '#222343' }}>Após autorização tempo médio de confirmação é de 2 minutos</span>
                </div>
                <button
                    type="button"
                    className={`btn-ghost-sim ${styles.btnAtualizar}`}
                    style={{ flexShrink: 0, whiteSpace: 'nowrap' }}
                    onClick={handleAtualizar}
                    disabled={cooldown > 0}
                >
                    <img src={refreshIcon} alt="" className={styles.refreshIcon} />
                    <span className={styles.refreshLabel}>Atualizar Financeira</span>
                    {cooldown > 0 && (
                        <span className={styles.refreshCooldown}>({cooldown}s)</span>
                    )}
                </button>
            </div>

            {!hasAnyCards && (
                <div className="step-cards__polling-notice">
                    <span className="step-cards__polling-dot me-2" />
                    Carregando financeiras disponíveis...
                </div>
            )}

            {isPolling && hasAnyCards && (
                <div className="step-cards__polling-notice">
                    <span className="step-cards__polling-dot me-2" />
                    Atualizando status das financeiras em tempo real...
                </div>
            )}

            {pendentes.length > 0 && (
                <div className={`fin-category ${pendenteOpen ? 'open' : ''}`}>
                    <div className="fin-category-header fin-category-header--pending" onClick={() => setPendenteOpen(!pendenteOpen)}>
                        <div className="fin-category-header__left">
                            <span className="fin-pending-dot" />
                            <div className={styles.tinyLineHeight}>
                                <span className="fin-category-title fin-category-title--pending">Aguardando autorização do cliente para consultar a margem</span>
                            </div>
                        </div>
                        <ChevronIcon open={pendenteOpen} />
                    </div>
                    {pendenteOpen && (
                        <div className="fin-category-body fin-category-body--pending">
                            <InfoWarning message="Após o processamento da autorização, ela avançará para a fase de simulação." />
                            {pendentes.map((card) => (
                                <FinanceiraCardItem
                                    key={card.id}
                                    card={card}
                                    onToggle={toggleFinanceira}
                                    isPending={true}
                                    isRefreshing={cooldown > 0}
                                    clientPhone={formData?.telefone}
                                    bmgValidating={isValidatingBmg}
                                    onBmgCodeSubmit={handleValidateBmgCode}
                                />
                            ))}
                        </div>
                    )}
                </div>
            )}

            {hasResults && hasOptionalData && (
                <>
                    {alta.length > 0 && (
                        <div className={`fin-category ${altaOpen ? 'open' : ''}`}>
                            <div className="fin-category-header" onClick={() => setAltaOpen(!altaOpen)}>
                                <div className="fin-category-header__left">
                                    <SortIcon />
                                    <span className="fin-category-title fin-category-title--alta">Maior probabilidade de aprovação</span>
                                </div>
                                <ChevronIcon open={altaOpen} />
                            </div>
                            {altaOpen && (
                                <div className="fin-category-body fin-category-body--alta">
                                    <InfoWarning message="O cliente atende aos critérios do roteiro operacional" />
                                    {alta.map((card) => (
                                        <FinanceiraCardItem key={card.id} card={card} onToggle={toggleFinanceira} />
                                    ))}
                                </div>
                            )}
                        </div>
                    )}
                    {baixa.length > 0 && (
                        <div className={`fin-category ${baixaOpen ? 'open' : ''}`}>
                            <div className="fin-category-header" onClick={() => setBaixaOpen(!baixaOpen)}>
                                <div className="fin-category-header__left">
                                    <SortIcon />
                                    <span className="fin-category-title fin-category-title--baixa">Menor probabilidade de aprovação</span>
                                </div>
                                <ChevronIcon open={baixaOpen} />
                            </div>
                            {baixaOpen && (
                                <div className="fin-category-body fin-category-body--baixa">
                                    <InfoWarning message="O cliente não atende aos critérios do roteiro operacional, porém é válido a simulação no Crédito CLT por motivo de análise interna do banco." />
                                    {baixa.map((card) => (
                                        <FinanceiraCardItem key={card.id} card={card} onToggle={toggleFinanceira} />
                                    ))}
                                </div>
                            )}
                        </div>
                    )}
                </>
            )}

            {hasResults && !hasOptionalData && disponivel.length > 0 && (
                <div className={`fin-category ${disponivelOpen ? 'open' : ''}`}>
                    <div className="fin-category-header" onClick={() => setDisponivelOpen(!disponivelOpen)}>
                        <div className="fin-category-header__left">
                            <span className="fin-category-title fin-category-title--disponivel">Disponíveis para Simulação</span>
                        </div>
                        <ChevronIcon open={disponivelOpen} />
                    </div>
                    {disponivelOpen && (
                        <div className="fin-category-body fin-category-body--disponivel">
                            <InfoWarning message="O banco aprovou a consulta para este cliente." />
                            {disponivel.map((card) => (
                                <FinanceiraCardItem key={card.id} card={card} onToggle={toggleFinanceira} />
                            ))}
                        </div>
                    )}
                </div>
            )}

            {semOferta.length > 0 && (
                <div className={styles.semOfertaSection}>
                    <div className="sim-intro-message" style={{ padding: 0 }}>
                        <p className="sim-intro-stream">
                            <StreamingText segments={SEM_OFERTA_INTRO} />
                        </p>
                    </div>
                    <div className="fin-list">
                        {semOferta.map((card) => (
                            <FinanceiraCardItem key={card.id} card={card} onToggle={toggleFinanceira} semOferta />
                        ))}
                    </div>
                </div>
            )}

            <StepNavigation
                onBack={onBack}
                onNext={onSimulate}
                nextLabel="Simular Valores"
                nextDisabled={selecionadas.length === 0}
            />
        </div>
    );
};

export default Step3Cards;
